import { useCallback } from "react";
import { useSlotDialogParam } from "hooks/useSlotDialogParam";
import { type Event, type SlotApiRes } from "core/types";
import { type RouterInput, ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQueryClient } = ReactQueryV5;
import { getUnixTime } from "date-fns";
import { type TFunction } from "i18next";
import { useMindlerApi } from "api/apiv2";
import { useAlertContext } from "../AlertContext";
import { useInvalidateSlots } from "./useInvalidateSlots";

export const useHandleDropPickerUpdate = () => {
  const [, setSlotIdParam] = useSlotDialogParam();
  const { showAlert } = useAlertContext();
  const invalidateSlots = useInvalidateSlots();
  const queryClient = useQueryClient();
  const api = useMindlerApi();

  return useCallback(
    async (
      updatedEvent: Event & { startDate: Date; endDate: Date },
      t: TFunction,
      psychologistUserId: number,
      moveSlot: (
        args: RouterInput["slots"]["psychologist"]["move"],
      ) => Promise<void>,
    ) => {
      queryClient.setQueriesData<SlotApiRes[]>(
        api.slots.psychologist.calendar.queryFilter(),
        (old) => {
          if (!old) return old;
          return old.map((s) =>
            s.slotId === updatedEvent.event.slotId
              ? {
                  ...s,
                  startsAt: getUnixTime(updatedEvent.startDate),
                  endsAt: getUnixTime(updatedEvent.endDate),
                }
              : s,
          );
        },
      );
      try {
        await moveSlot({
          startsAt: updatedEvent.startDate,
          endsAt: updatedEvent.endDate,
          slotId: updatedEvent.event.slotId,
          psychologistUserId,
        });
        await setSlotIdParam(null);
      } catch (err: unknown) {
        if ((err as { errorLangKey?: string })?.errorLangKey) {
          showAlert(t((err as { errorLangKey: string }).errorLangKey));
        }
      } finally {
        await invalidateSlots();
      }
    },
    [setSlotIdParam, showAlert, invalidateSlots, queryClient, api],
  );
};
